import { SLIDER_CONFIG, SELECTORS } from './constants';
import type { ObserverTracking, HTMLElementWithObserver } from './types';

/**
 * Arrow Visibility Manager
 * Handles showing/hiding desktop vs mobile navigation arrows
 */
export default class ArrowVisibilityManager {
    component: JQuery<HTMLElement>;
    mobileQuery: MediaQueryList;
    observers: ObserverTracking[];

    /**
     * @param component - Main component container
     */
    constructor(component: JQuery<HTMLElement>) {
        this.component = component;
        this.mobileQuery = window.matchMedia(SLIDER_CONFIG.MOBILE_BREAKPOINT);
        this.observers = [];
    }

    /**
     * Sets visibility of arrows based on viewport size
     * @param isMobile - Whether we're in mobile viewport
     */
    setVisibility(isMobile: boolean): void {
        const controls = this.component.find(SELECTORS.CONTROL_BUTTONS);

        if (isMobile) {
            this._handleMobileArrows(controls);
        } else {
            this._handleDesktopArrows(controls);
        }

        // Ensure active arrows are visible after layout change
        setTimeout(() => this._ensureActiveArrowsVisible(), 0);
    }

    /**
     * Observes an arrow element for class/style changes
     * @param element - Arrow element to observe
     * @param _identifier - Debug identifier (unused, kept for API compatibility)
     */
    observeArrow(element: JQuery<HTMLElement>, _identifier: string): void {
        if (!element || !element.length) return;

        const target = element[0] as HTMLElementWithObserver;
        if (target.__featureArticleObserver) return;

        const observer = new MutationObserver(() => {
            this._ensureActiveArrowsVisible();
        });

        observer.observe(target, {
            attributes: true,
            attributeFilter: ['class', 'style']
        });

        target.__featureArticleObserver = observer;
        this.observers.push({ element: target, observer: observer });
    }

    /**
     * Disconnects all mutation observers
     */
    cleanup(): void {
        this.observers.forEach(({ element, observer }: ObserverTracking) => {
            observer.disconnect();
            delete (element as HTMLElementWithObserver).__featureArticleObserver;
        });
        this.observers = [];
    }

    private _handleMobileArrows(controls: JQuery<HTMLElement>): void {
        const desktopPrev = this.component.find(SELECTORS.PREV_ARROW);
        const desktopNext = this.component.find(SELECTORS.NEXT_ARROW);

        this._detachIfPresent(desktopPrev, 'featureArticleDesktopPrev');
        this._detachIfPresent(desktopNext, 'featureArticleDesktopNext');
        this._attachIfMissing('featureArticleMobilePrev', controls);
        this._attachIfMissing('featureArticleMobileNext', controls);

        const mobilePrev = this.component.find(SELECTORS.MOBILE_PREV_ARROW);
        const mobileNext = this.component.find(SELECTORS.MOBILE_NEXT_ARROW);
        mobilePrev.removeClass('slick-hidden slick-disabled').removeAttr('style');
        mobileNext.removeClass('slick-hidden slick-disabled').removeAttr('style');
    }

    private _handleDesktopArrows(controls: JQuery<HTMLElement>): void {
        const mobilePrev = this.component.find(SELECTORS.MOBILE_PREV_ARROW);
        const mobileNext = this.component.find(SELECTORS.MOBILE_NEXT_ARROW);

        this._detachIfPresent(mobilePrev, 'featureArticleMobilePrev');
        this._detachIfPresent(mobileNext, 'featureArticleMobileNext');
        this._attachIfMissing('featureArticleDesktopPrev', controls);
        this._attachIfMissing('featureArticleDesktopNext', controls);

        const desktopPrev = this.component.find(SELECTORS.PREV_ARROW);
        const desktopNext = this.component.find(SELECTORS.NEXT_ARROW);
        desktopPrev.removeClass('slick-hidden slick-disabled').removeAttr('style');
        desktopNext.removeClass('slick-hidden slick-disabled').removeAttr('style');
    }

    private _detachIfPresent(element: JQuery<HTMLElement>, dataKey: string): void {
        if (element && element.length && !this.component.data(dataKey)) {
            this.component.data(dataKey, element.detach());
        }
    }

    private _attachIfMissing(dataKey: string, container: JQuery<HTMLElement>): void {
        const cached = this.component.data(dataKey) as JQuery<HTMLElement> | undefined;
        if (cached && cached.length) {
            container.append(cached);
            this.component.removeData(dataKey);
        }
    }

    private _ensureActiveArrowsVisible(): void {
        const isMobile = this.mobileQuery.matches;
        const activeArrows = isMobile
            ? this.component.find(`${SELECTORS.MOBILE_PREV_ARROW}, ${SELECTORS.MOBILE_NEXT_ARROW}`)
            : this.component.find(`${SELECTORS.PREV_ARROW}, ${SELECTORS.NEXT_ARROW}`);

        activeArrows.removeClass('slick-hidden slick-disabled').removeAttr('style');
    }
}

